import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, btn, EmptyState } from "@/components/kit";
import { toast } from "sonner";
import { Plus, Workflow, Users, Pencil, Trash2, Copy, LayoutTemplate } from "lucide-react";

export default function Flows() {
  const nav = useNavigate();
  const [flows, setFlows] = useState([]);
  const [templates, setTemplates] = useState([]);

  const load = () => {
    api.get("/flows").then((r) => setFlows(r.data)).catch(() => {});
    api.get("/flow-templates").then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const applyTemplate = async (t) => {
    try {
      const { data } = await api.post("/flows", { name: t.name, template_id: t.id });
      toast.success(`Created "${data.name}"`);
      nav(`/app/flows/${data.id}/edit`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete flow "${name}"?`)) return;
    try { await api.delete(`/flows/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const taskCount = (f) => (f.stages || []).reduce((a, s) => a + (s.tasks || []).length, 0);

  return (
    <div>
      <PageHeader title="Onboarding flows" subtitle="Design the journeys your clients complete.">
        <button className={btn.primary} onClick={() => nav("/app/flows/new")} data-testid="create-flow-button"><Plus className="h-4 w-4" /> New flow</button>
      </PageHeader>

      {flows.length === 0 ? (
        <EmptyState icon={Workflow} title="No flows yet" subtitle="Start from a ready-made industry template below, or build your own from scratch." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {flows.map((f) => (
            <div key={f.id} className="bg-card border border-border p-6 hover:shadow-hard transition-shadow group" data-testid={`flow-card-${f.id}`}>
              <div className="flex items-start justify-between">
                <span className="grid place-items-center h-10 w-10 bg-secondary"><Workflow className="h-5 w-5" /></span>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => nav(`/app/flows/${f.id}/edit`)} className="p-1.5 hover:bg-secondary" data-testid={`flow-edit-${f.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(f.id, f.name)} className="p-1.5 hover:bg-secondary text-destructive" data-testid={`flow-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <h3 className="text-lg font-semibold mt-4">{f.name}</h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2 min-h-[2.5rem]">{f.description || "No description"}</p>
              <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground border-t border-border pt-4">
                <span>{(f.stages || []).length} stages</span>
                <span>{taskCount(f)} tasks</span>
                <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {f.client_count} clients</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <LayoutTemplate className="h-5 w-5 text-accent" />
        <h2 className="text-lg font-bold">Start from a template</h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {templates.map((t) => (
          <div key={t.id} className="bg-card border border-border p-6 flex flex-col" data-testid={`template-card-${t.id}`}>
            <h3 className="font-semibold">{t.name}</h3>
            <p className="text-sm text-muted-foreground mt-1 flex-1 line-clamp-3">{t.description}</p>
            <p className="text-xs text-muted-foreground mt-3">{(t.stages || []).length} stages · {(t.stages || []).reduce((a, s) => a + s.tasks.length, 0)} tasks</p>
            <button onClick={() => applyTemplate(t)} className={`${btn.outline} w-full mt-4`} data-testid={`template-use-${t.id}`}><Copy className="h-4 w-4" /> Use template</button>
          </div>
        ))}
      </div>
    </div>
  );
}
